<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-lg-8">
        <h4>Sponsor Donations</h4>
        <p class="text-muted">Manage large sponsor donations. Create them in advance and activate when ready.</p>
      </div>
    </div>

    <!-- Create New Sponsor Donation -->
    <div class="row mt-4">
      <div class="col-lg-6">
        <Card>
          <template #header><h5>Add Sponsor Donation</h5></template>
          <template #content>
            <div class="field mb-3">
              <label>Company / Sponsor Name</label>
              <input
                v-model="newDonation.companyName"
                class="form-control"
                placeholder="Enter company name"
              />
            </div>

            <div class="field mb-3">
              <label>Donation Amount</label>
              <input
                v-model="newDonation.formattedAmount"
                @input="formatNewAmount"
                class="form-control"
                placeholder="Enter amount"
              />
            </div>

            <div class="field mb-3">
              <label>Currency</label>
              <select v-model="newDonation.currency" class="form-control">
                <option value="SEK">SEK</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>

            <div class="field mb-3">
              <label>Link to Donation Item (optional)</label>
              <Dropdown
                v-model="newDonation.donationItem"
                :options="donationItems"
                optionLabel="name"
                placeholder="Uses default Sponsor Donations item"
                class="w-100"
                :showClear="true"
              />
              <small class="text-muted">Leave empty to use the default "Sponsor Donations" item.</small>
            </div>

            <div class="d-flex align-items-center gap-3 mb-3">
              <InputSwitch v-model="newDonation.activateNow" />
              <span>{{ newDonation.activateNow ? 'Activate immediately' : 'Save as draft (activate later)' }}</span>
            </div>
          </template>
          <template #footer>
            <ArgonButton
              type="button"
              variant="gradient"
              class="btn-success"
              @click="createDonation"
              :disabled="saving"
            >
              {{ newDonation.activateNow ? 'Save & Activate' : 'Save Draft' }}
            </ArgonButton>
          </template>
        </Card>
      </div>
    </div>

    <!-- List of Sponsor Donations -->
    <div class="row mt-5">
      <div class="col-12">
        <Card>
          <template #header><h5>All Sponsor Donations</h5></template>
          <template #content>
            <div v-if="sponsorDonations.length === 0" class="text-muted">
              No sponsor donations yet.
            </div>
            <!-- Desktop table -->
            <div v-if="sponsorDonations.length > 0" class="d-none d-md-block">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Amount</th>
                    <th>Donation Item</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="donation in sponsorDonations" :key="donation.id">
                    <td>{{ donation.companyName }}</td>
                    <td>{{ formatPrice(donation.amount) }} {{ donation.currency }}</td>
                    <td>{{ donation.donationItemName || 'Not linked' }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="donation.active ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ donation.active ? 'Active' : 'Draft' }}
                      </span>
                    </td>
                    <td>{{ formatDate(donation.createdAt) }}</td>
                    <td>
                      <button
                        v-if="!donation.active"
                        class="btn btn-sm btn-success me-2"
                        @click="activateDonation(donation)"
                        :disabled="saving"
                      >
                        Activate
                      </button>
                      <button
                        v-if="donation.active"
                        class="btn btn-sm btn-warning me-2"
                        @click="deactivateDonation(donation)"
                        :disabled="saving"
                      >
                        Deactivate
                      </button>
                      <button
                        class="btn btn-sm btn-danger"
                        @click="deleteDonation(donation)"
                        :disabled="saving"
                      >
                        Delete
                      </button>
                      <span v-if="donation.active" class="text-muted ms-2">
                        Activated {{ formatDate(donation.activatedAt) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Mobile cards -->
            <div v-if="sponsorDonations.length > 0" class="d-md-none">
              <div
                v-for="donation in sponsorDonations"
                :key="donation.id"
                class="card mb-3 shadow-sm"
              >
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h6 class="mb-0 fw-bold">{{ donation.companyName }}</h6>
                    <span
                      class="badge"
                      :class="donation.active ? 'bg-success' : 'bg-secondary'"
                    >
                      {{ donation.active ? 'Active' : 'Draft' }}
                    </span>
                  </div>
                  <p class="mb-1"><strong>Amount:</strong> {{ formatPrice(donation.amount) }} {{ donation.currency }}</p>
                  <p class="mb-1 text-muted small">
                    <strong>Item:</strong> {{ donation.donationItemName || 'Not linked' }}
                  </p>
                  <p class="mb-2 text-muted small">
                    <strong>Created:</strong> {{ formatDate(donation.createdAt) }}
                    <span v-if="donation.active">
                      &middot; <strong>Activated:</strong> {{ formatDate(donation.activatedAt) }}
                    </span>
                  </p>
                  <div class="d-flex flex-wrap gap-2">
                    <button
                      v-if="!donation.active"
                      class="btn btn-sm btn-success"
                      @click="activateDonation(donation)"
                      :disabled="saving"
                    >
                      Activate
                    </button>
                    <button
                      v-if="donation.active"
                      class="btn btn-sm btn-warning"
                      @click="deactivateDonation(donation)"
                      :disabled="saving"
                    >
                      Deactivate
                    </button>
                    <button
                      class="btn btn-sm btn-danger"
                      @click="deleteDonation(donation)"
                      :disabled="saving"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/firebase";
import Card from "primevue/card";
import Dropdown from "primevue/dropdown";
import InputSwitch from "primevue/inputswitch";
import ArgonButton from "@/components/ArgonButton.vue";
import { useToast } from "vue-toastification";

const toast = useToast();
const saving = ref(false);

const donationItems = ref([]);
const sponsorDonations = ref([]);

const newDonation = ref({
  companyName: "",
  formattedAmount: "",
  rawAmount: "",
  currency: "SEK",
  donationItem: null,
  activateNow: false,
});

const formatNewAmount = (event) => {
  const rawValue = event.target.value.replace(/,/g, "");
  newDonation.value.rawAmount = rawValue;
  const num = Number(rawValue);
  if (!isNaN(num) && rawValue !== "") {
    newDonation.value.formattedAmount = num.toLocaleString("en-US");
  }
};

const formatPrice = (value) => {
  return Number(value).toLocaleString("en-US");
};

const formatDate = (timestamp) => {
  if (!timestamp) return "—";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const defaultDonationItem = ref(null);

const getOrCreateDefaultDonationItem = async () => {
  // Look for an existing default sponsor donation item
  const q = query(
    collection(db, "auctionItems"),
    where("isSponsorDefault", "==", true)
  );
  const snapshot = await getDocs(q);

  if (snapshot.size > 0) {
    const docSnap = snapshot.docs[0];
    defaultDonationItem.value = { id: docSnap.id, name: docSnap.data().name };
    return;
  }

  // Find highest itemNumber to assign next
  const allItemsSnap = await getDocs(collection(db, "auctionItems"));
  let maxNumber = 0;
  allItemsSnap.forEach((d) => {
    const num = parseInt(d.data().itemNumber) || 0;
    if (num > maxNumber) maxNumber = num;
  });

  // Create the default donation item
  const newItem = {
    name: "Sponsor Donations",
    itemNumber: String(maxNumber + 1),
    category: "",
    startbid: "0",
    latestBid: 0,
    latestBidder: "",
    latestBidderID: "",
    enddate: null,
    description: "Default item for sponsor donations",
    creator: "",
    imageUrl: "",
    resizedImages: [],
    imageGallery: [],
    estimatedValue: 0,
    priceless: true,
    currency: "SEK",
    size: "",
    other: "",
    liveAuction: false,
    isBuyNowEnabled: false,
    buyNowPrice: "0",
    hasBuyNowBid: false,
    isDonationItem: true,
    isSponsorDefault: true,
    winnerSMSSent: false,
    bidCount: 0,
  };

  const docRef = await addDoc(collection(db, "auctionItems"), newItem);
  defaultDonationItem.value = { id: docRef.id, name: "Sponsor Donations" };
  toast.info('Created default "Sponsor Donations" auction item.');
};

const fetchDonationItems = async () => {
  const q = query(
    collection(db, "auctionItems"),
    where("isDonationItem", "==", true)
  );
  const snapshot = await getDocs(q);
  donationItems.value = snapshot.docs.map((d) => ({
    id: d.id,
    name: d.data().name,
  }));
};

const listenToSponsorDonations = () => {
  const q = query(
    collection(db, "sponsorDonations"),
    orderBy("createdAt", "desc")
  );
  onSnapshot(q, (snapshot) => {
    sponsorDonations.value = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  });
};

const createDonation = async () => {
  const { companyName, rawAmount, currency, donationItem, activateNow } = newDonation.value;

  if (!companyName || !rawAmount) {
    toast.error("Please fill in company name and amount.");
    return;
  }

  const amount = Number(rawAmount);
  if (isNaN(amount) || amount <= 0) {
    toast.error("Please enter a valid amount.");
    return;
  }

  saving.value = true;
  try {
    // Use selected item or fall back to default
    const targetItem = donationItem || defaultDonationItem.value;

    const donationData = {
      companyName,
      amount,
      currency,
      donationItemId: targetItem?.id || null,
      donationItemName: targetItem?.name || null,
      active: activateNow,
      createdAt: Timestamp.now(),
      activatedAt: activateNow ? Timestamp.now() : null,
    };

    const sponsorDocRef = await addDoc(collection(db, "sponsorDonations"), donationData);

    if (activateNow && targetItem) {
      const purchaseDocId = await writePurchase(targetItem.id, companyName, amount, currency);
      await updateDoc(doc(db, "sponsorDonations", sponsorDocRef.id), { purchaseDocId });
    }

    // Reset form
    newDonation.value = {
      companyName: "",
      formattedAmount: "",
      rawAmount: "",
      currency: "SEK",
      donationItem: null,
      activateNow: false,
    };

    toast.success(activateNow ? "Sponsor donation created & activated!" : "Sponsor donation saved as draft.");
  } catch (error) {
    console.error("Error creating sponsor donation:", error);
    toast.error("Failed to create sponsor donation.");
  } finally {
    saving.value = false;
  }
};

const activateDonation = async (donation) => {
  saving.value = true;
  try {
    const updateData = {
      active: true,
      activatedAt: Timestamp.now(),
    };

    // Use existing linked item or fall back to default
    const targetItemId = donation.donationItemId || defaultDonationItem.value?.id;
    if (targetItemId) {
      const purchaseDocId = await writePurchase(
        targetItemId,
        donation.companyName,
        donation.amount,
        donation.currency
      );
      updateData.purchaseDocId = purchaseDocId;
      updateData.donationItemId = targetItemId;
      updateData.donationItemName = donation.donationItemName || defaultDonationItem.value?.name;
    }

    await updateDoc(doc(db, "sponsorDonations", donation.id), updateData);

    toast.success(`Sponsor donation from ${donation.companyName} activated!`);
  } catch (error) {
    console.error("Error activating sponsor donation:", error);
    toast.error("Failed to activate sponsor donation.");
  } finally {
    saving.value = false;
  }
};

const deactivateDonation = async (donation) => {
  if (!confirm(`Deactivate donation from ${donation.companyName}?`)) return;

  saving.value = true;
  try {
    // Remove the linked purchase document if it exists
    if (donation.purchaseDocId && donation.donationItemId) {
      await deleteDoc(doc(db, `auctionItems/${donation.donationItemId}/purchase`, donation.purchaseDocId));
    }

    await updateDoc(doc(db, "sponsorDonations", donation.id), {
      active: false,
      activatedAt: null,
      purchaseDocId: null,
    });

    toast.success(`Sponsor donation from ${donation.companyName} deactivated.`);
  } catch (error) {
    console.error("Error deactivating sponsor donation:", error);
    toast.error("Failed to deactivate donation.");
  } finally {
    saving.value = false;
  }
};

const writePurchase = async (donationItemId, companyName, amount, currency) => {
  const purchaseRef = collection(db, `auctionItems/${donationItemId}/purchase`);
  const docRef = await addDoc(purchaseRef, {
    comment: "Sponsor Donation",
    itemName: "Sponsor Donation",
    timestamp: Timestamp.now(),
    userBid: amount,
    currency: currency || "SEK",
    user: {
      name: companyName,
      table: "Sponsor",
      tableId: "",
    },
  });
  return docRef.id;
};

const deleteDonation = async (donation) => {
  const label = donation.active ? 'activated' : 'draft';
  if (!confirm(`Delete ${label} donation from ${donation.companyName}?`)) return;

  saving.value = true;
  try {
    // If activated, also remove the linked purchase document
    if (donation.active && donation.purchaseDocId && donation.donationItemId) {
      await deleteDoc(doc(db, `auctionItems/${donation.donationItemId}/purchase`, donation.purchaseDocId));
    }
    await deleteDoc(doc(db, "sponsorDonations", donation.id));
    toast.success(`Sponsor donation from ${donation.companyName} deleted.`);
  } catch (error) {
    console.error("Error deleting sponsor donation:", error);
    toast.error("Failed to delete donation.");
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  await getOrCreateDefaultDonationItem();
  await fetchDonationItems();
  listenToSponsorDonations();
});
</script>

<style scoped>
.field label {
  font-weight: 600;
  margin-bottom: 0.25rem;
  display: block;
}
</style>
